import { ScreenBase } from "@frui.ts/screens/dist";
import { action, computed, observable } from "mobx";
import { PersistedBase64 } from "models/notice";
import LocalizationService from "services/localizationService";
import printJS from "print-js";
import { interfaces } from "inversify";
import { Notice } from "../../../services/NIA/niaService";
import { getFile } from "../../../data/indexedDbConnector";
import { base64ToFile } from "../../../utils/base64";
import { saveFileFromURL } from "utils/fileUtils";
import { bound } from "@frui.ts/helpers/dist";

export default class DocumentsUploadedViewModel extends ScreenBase {
  private titleRegExp = new RegExp(/%title%/g);
  private descriptionRegExp = new RegExp(/%description%/g);

  @observable documentUrl?: string;

  public translateGeneral = (code: string) => {
    // Refund request has its own translations of the success screen. Overriding this method
    // in RefundRequestDocumentsUploadedViewModel doesn't work for some reason, so we need to override here
    if (this.type === Notice.REFUND_REQUEST) {
      return this.localizationService.translateGeneral(`refundRequest.documentsUploaded.${code}`);
    }
    return this.localizationService.translateGeneral(`documentsUploaded.${code}`);
  };

  constructor(protected notice: PersistedBase64, protected type: Notice, public localizationService: LocalizationService) {
    super();
    this.navigationName = "documents";
    (async () => {
      const data = await getFile(this.notice.id);

      if (data) {
        const file = await base64ToFile(data.base64!, data.fileName);
        this.documentUrl = window.URL.createObjectURL(file);
      }
    })();
  }

  private noticeTitle = (noticeType: Notice) => {
    let key = "exemption";
    switch (noticeType) {
      case Notice.EXEMPTION:
        key = "exemption";
        break;
      case Notice.OMISSION:
        key = "omission";
        break;
      case Notice.LICENSE_PLATE_CHANGE:
        key = "licensePlateChange";
        break;
      case Notice.REFUND_REQUEST:
        key = "refundRequest";
    }

    return this.localizationService.translateGeneral(`${key}.title`);
  };

  @computed
  get title() {
    return this.translateGeneral("title").replace(this.titleRegExp, this.noticeTitle(this.type));
  }

  @computed
  get description() {
    return this.translateGeneral("description").replace(this.descriptionRegExp, this.noticeTitle(this.type));
  }

  @computed
  get canProceedToExternalService() {
    return false;
  }

  @computed
  get isContinueDisabled(): boolean {
    return false;
  }

  @computed
  get canPrintNotice() {
    return true;
  }

  @computed
  get canSaveNoticePDF() {
    return true;
  }

  @computed
  get canCancelNotice() {
    return false;
  }

  @action.bound
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  cancel() {}

  @action.bound
  async savePDF() {
    if (!this.documentUrl) return;

    await saveFileFromURL(this.getDocumentURL, "Uploaded documents", {
      notice: this.notice,
    });
  }

  @bound
  getDocumentURL() {
    const promise = Promise.resolve(this.documentUrl);
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    promise.catch(() => {});
    return promise;
  }

  @action.bound
  async printPDF() {
    if (!this.documentUrl) return;

    printJS(this.documentUrl);
  }

  @action.bound
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  continue() {}

  static Factory({ container }: interfaces.Context) {
    return (notice: PersistedBase64, type: Notice) => {
      return new DocumentsUploadedViewModel(notice, type, container.get(LocalizationService));
    };
  }
}
